/**
 * Attribution tracking utilities for capturing and persisting UTM parameters
 * and referrer information across subdomain navigation
 */

export interface AttributionData {
  landing_url?: string;
  original_referrer?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
}

const ATTRIBUTION_COOKIE_NAME = "msgme_attribution";
const COOKIE_EXPIRY_DAYS = 30;

/**
 * Captures current attribution data from URL and referrer
 */
export function captureAttributionData(): AttributionData {
  const urlParams = new URLSearchParams(window.location.search);
  const attributionData: AttributionData = {};

  /** Capture UTM parameters */
  const utmParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ] as const;

  utmParams.forEach((param) => {
    const value = urlParams.get(param);

    if (value) {
      attributionData[param] = value;
    }
  });

  /** Capture referrer if available */
  if (document.referrer) {
    attributionData.original_referrer = document.referrer;
  }

  /** Capture full landing page URL */
  attributionData.landing_url = window.location.href;

  return attributionData;
}

/**
 * Stores attribution data in a cookie accessible across subdomains
 */
export function storeAttributionInCookie(data: AttributionData): void {
  const expiryDate = new Date();

  expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);

  const cookieValue = encodeURIComponent(JSON.stringify(data));
  const cookieString = `${ATTRIBUTION_COOKIE_NAME}=${cookieValue}; domain=.msg-me.org; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax; Secure`;

  document.cookie = cookieString;
}

/**
 * Appends attribution data as query parameters to a URL
 */
export function appendAttributionToUrl(
  baseUrl: string,
  data: AttributionData,
): string {
  const url = new URL(baseUrl);

  Object.entries(data).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

/**
 * Initializes attribution tracking on landing page
 * Call this on page load to capture and persist attribution data
 */
export function initializeAttributionTracking(): void {
  const attributionData = captureAttributionData();

  /** Only store if we have any attribution data */
  if (Object.keys(attributionData).length > 0) {
    storeAttributionInCookie(attributionData);
  }
}
